/**
 * Socket.IO client factory for the public site.
 *
 * The tracking backend exposes a realtime namespace (`/tracking`) that the
 * customer tracker connects to *without* a login — the gateway allows
 * tokenless sockets and only ever pushes lightweight "refetch" signals, never
 * org-scoped data. One shared socket is kept per namespace so repeated hook
 * mounts (e.g. re-searching) reuse the same connection.
 */
import { io, Socket } from 'socket.io-client';

// Same base as the REST client — CRA only exposes `REACT_APP_`-prefixed vars.
const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';

const sockets = new Map<string, Socket>();

/**
 * Get (or lazily create) the shared socket for a namespace. The socket is
 * created disconnected — the caller connects when it has something to watch.
 */
export function getNamespacedSocket(namespace: string): Socket {
  const existing = sockets.get(namespace);
  if (existing) return existing;

  const socket = io(`${BASE}${namespace}`, {
    transports: ['websocket', 'polling'],
    autoConnect: false,
    // Public tracker — no auth token. The gateway treats tokenless sockets as
    // public viewers.
    reconnection: true,
  });
  sockets.set(namespace, socket);
  return socket;
}

/** Tear down and forget the shared socket for a namespace. */
export function disconnectNamespacedSocket(namespace: string): void {
  const socket = sockets.get(namespace);
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  sockets.delete(namespace);
}
